import React, { useContext, useState } from 'react'
//@ts-ignore
import JSONEditor from 'jsoneditor'
import 'jsoneditor/dist/jsoneditor.css'
import ReactJson from 'react-json-view'
import {
    ButtonType,
    CustomButton,
    Empty,
    FormSavePrompt,
    InfoTooltip,
    OverlaySpinner,
    Section,
    ToastTypes,
    launchToast
} from '../../../components'
import { useFormSaveStore } from '../../../utils'
import { DeviceContext } from '../DeviceContext'
import styles from './ExpertConfig.module.css'

export const ExpertConfig = () => {
    const [diff, setDiff] = useState<any>()
    const [isValidJson, setIsValidJson] = useState(true)
    const deviceContext = useContext(DeviceContext)
    const setFormChanges = useFormSaveStore((state) => state.setFormChanges)
    const hasChanges = useFormSaveStore((state) => state.hasChanges)

    const forceUpdateDisabled =
        !!deviceContext.deviceData?.config_force_update ||
        deviceContext.deviceData?.config_current_timestamp === deviceContext.deviceData?.config_target_timestamp

    const editorRef = React.useRef<HTMLDivElement>(null)
    const jsonEditor = React.useRef<JSONEditor | null>(null)

    React.useEffect(() => {
        // Initialize JSONEditor
        if (editorRef.current && deviceContext.deviceData) {
            jsonEditor.current = new JSONEditor(editorRef.current, {
                mode: 'code',
                onChange: () => {
                    try {
                        const updatedJson = jsonEditor.current?.get()
                        JSON.parse(JSON.stringify(updatedJson)) // Validate JSON
                        setDiff(updatedJson)
                        setIsValidJson(true)
                        setFormChanges(true)
                    } catch (err) {
                        setIsValidJson(false)
                    }
                }
            })

            // Set initial data from deviceContext
            jsonEditor.current.set(deviceContext.deviceData.config.diff || {})

            // Cleanup on component unmount
            return () => {
                if (jsonEditor.current) {
                    jsonEditor.current.destroy()
                }
            }
        }
    }, [editorRef.current, deviceContext.deviceData]) // Re-initialize when deviceData changes

    if (!deviceContext.deviceData) {
        return <Empty title="Select a device first!" />
    }
    if (!deviceContext.deviceData.config) {
        return <>Config not found</>
    }
    return (
        <div className={styles.root}>
            <FormSavePrompt />
            <div className={styles.actions}>
                <CustomButton
                    type={ButtonType.SAVE}
                    disabled={
                        !hasChanges ||
                        !isValidJson ||
                        deviceContext.loadingSaveBasicConfig ||
                        deviceContext.loadingForceUpdate
                    }
                    isLoading={deviceContext.loadingSaveBasicConfig}
                    onClick={() => {
                        if (!diff) {
                            launchToast(ToastTypes.Error, 'No new edit in diff!')
                            return
                        }
                        if (!isValidJson) {
                            launchToast(ToastTypes.Error, 'Invalid JSON format!')
                            return
                        }
                        setFormChanges(false)

                        deviceContext.editDiffConfig(deviceContext.selectedDevice?.uuid, diff, () => {
                            setDiff(undefined)
                        })
                    }}
                >
                    Save Diff
                </CustomButton>
                &nbsp;&nbsp;&nbsp;
                <InfoTooltip content={forceUpdateDisabled ? 'No changes to force update' : null}>
                    <CustomButton
                        type={ButtonType.DELETE}
                        disabled={
                            forceUpdateDisabled ||
                            deviceContext.loadingSaveBasicConfig ||
                            deviceContext.loadingForceUpdate
                        }
                        isLoading={deviceContext.loadingForceUpdate}
                        onClick={() => {
                            if (
                                window.confirm(
                                    'Are you sure you want to force update the configuration for this device?'
                                )
                            ) {
                                deviceContext.setDeviceForceUpdateAction(deviceContext.selectedDevice?.uuid)
                            }
                        }}
                    >
                        Force Update
                    </CustomButton>
                </InfoTooltip>
            </div>
            <OverlaySpinner
                loadingTop={true}
                loading={deviceContext.loadingSaveBasicConfig || deviceContext.loadingForceUpdate}
            >
                <div className={styles.body}>
                    <div className={styles.diffColumn}>
                        <Section headingLevel={5} title="Diff">
                            <div id="jsoneditor" ref={editorRef} className={styles.jsonEditor} />
                            {/* <JSONInput
                                id="a_unique_id"
                                theme={'light_mitsuketa_tribute'}
                                placeholder={diff || deviceContext.deviceData?.config.diff}
                                locale={locale}
                                height="550px"
                                onChange={(value: any) => {
                                    setFormChanges(FormSaveEnum.ExpertConfig, true)
                                    setDiff(value.jsObject)
                                }}
                                width="100%"
                            /> */}
                        </Section>
                    </div>
                    <div className={styles.baseColumn}>
                        <Section headingLevel={5} title="Base">
                            <ReactJson
                                style={{ minHeight: '550px' }}
                                collapsed={1}
                                src={deviceContext.deviceData?.config.base}
                            />
                        </Section>
                    </div>
                </div>
            </OverlaySpinner>
        </div>
    )
}
